import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
// type-coverage:ignore-next-line
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { form, required, email, minLength } from '@angular/forms/signals';
import { FormSendingDialogService } from '@shared/form-sending-dialog/form-sending-dialog.service';
import { catchError, EMPTY, tap } from 'rxjs';
import { ContactFormItem } from './contact-form.types';

@Injectable()
export class ContactFormService {
    private readonly http = inject(HttpClient);
    private readonly platformId = inject<string>(PLATFORM_ID);
    private readonly _currentStep = signal<number>(0);

    public readonly currentStep = this._currentStep.asReadonly();

    private readonly formSendingDialogService = inject(FormSendingDialogService);

    public form = form(
        signal({
            name: '' as string,
            email: '' as string,
            shortMessage: '' as string,
        }),
        (p) => {
            required(p.name);
            required(p.email);
            email(p.email);
            required(p.shortMessage);
            minLength(p.shortMessage, 5);
        }
    );

    public next(): void {
        const currentStep = this._currentStep();
        if (currentStep === this.steps.length - 1) {
            if (this.form().valid()) {
                this.sendEmail();
            }
            return;
        }
        this._currentStep.set(currentStep + 1);
    }

    public back(): void {
        const currentStep = this._currentStep();
        this._currentStep.set(currentStep - 1);
    }

    public readonly steps: ContactFormItem[] = [
        {
            formControlName: 'name',
            placeholder: 'Name & Surname',
        },
        {
            formControlName: 'email',
            placeholder: 'Email',
        },
        {
            formControlName: 'shortMessage',
            placeholder: 'Short Message',
            isTextarea: true,
        },
    ];

    public sendEmail(): void {
        if (this.form().invalid()) {
            this.form().markAsTouched();
            return;
        }
        const { name, email, shortMessage } = this.form().value();
        if (name && email && shortMessage) {
            this.formSendingDialogService.open();
            if (isPlatformServer(this.platformId)) {
                return;
            }
            this.http
                .post<string>('/send-email', { email, name, message: shortMessage })
                .pipe(
                    tap(() => {
                        this.resetForm();
                        return this.formSendingDialogService.changeToSuccess();
                    }),
                    catchError(() => {
                        this.resetForm();
                        this.formSendingDialogService.changeToError();
                        return EMPTY;
                    })
                )
                .subscribe();
        }
    }

    public resetForm(): void {
        this.form().reset();
        this._currentStep.set(0);
    }
}
